import { createContext, forwardRef, useCallback, useContext, useState } from "react";
import { useImperativeHandle } from "react";

const ManageMasterContext = createContext();

/**
 * <ManageMasterProvider>
 *
 * Context provider for multi-step "Manage Master" flows. Tracks current step,
 * handles navigation, exposes formik state, and provides imperative methods
 * via forwardRef.
 *
 * Props
 * - children (ReactNode): UI tree that consumes the context
 * - formik (FormikHelpers): formik instance for form state and validation
 * - isLoading (boolean): global loading flag for submissions
 * - paramId (string|number): product identifier (used in edit mode)
 * - isEdit (boolean): whether the form is in edit mode (default: false)
 * - initialStep (number): index of step to start from (default: 0)
 * - steps (Array<{title: string, desc: string}>): list of steps in the flow
 * - ref (React.Ref): forwarded ref exposing imperative stepper API
 *
 * Imperative Handle
 * - clearResult(): clears stored server result
 * - getActiveStep(): returns current step index
 * - getResult(): returns stored server result
 * - goToStep(idx: number): jumps to a specific step (bounded by max+1)
 * - setResult(data: any): stores server result
 * - toNextStep(): advance one step (bounded by max)
 * - toPreviousStep(): go back one step (bounded by 0)
 *
 * Returns
 * - ReactElement: context provider that supplies state and actions to children
 */
export const ManageMasterProvider = forwardRef(function ManageMasterProvider(
  { children, formik, isLoading, paramId, isEdit = false, initialStep = 0, steps = [] },
  ref,
) {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [serverResult, setServerResult] = useState(null);
  const [viewInstruction, setViewInstruction] = useState({ open: false, anchorEl: null });

  const maxIndex = steps.length ? steps.length - 1 : 0;

  const toPreviousStep = useCallback(() => setActiveStep(s => Math.max(0, s - 1)), []);

  const toNextStep = useCallback(() => setActiveStep(s => Math.min(maxIndex, s + 1)), [maxIndex]);

  const goToStep = useCallback(
    idx => {
      const n = Number(idx) || 0;
      setActiveStep(Math.min(Math.max(0, n), maxIndex + 1));
    },
    [maxIndex],
  );

  const toggleInsruction = useCallback(event => {
    const el = event.currentTarget;
    setViewInstruction(prev => ({
      ...prev,
      open: !prev.open,
      anchorEl: prev.anchorEl ? null : el,
    }));
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      clearResult: () => setServerResult(null),
      getActiveStep: () => activeStep,
      getResult: () => serverResult,
      goToStep,
      setResult: data => setServerResult(data),
      toNextStep,
      toPreviousStep,
    }),
    [toNextStep, toPreviousStep, activeStep, serverResult, goToStep],
  );

  return (
    <ManageMasterContext.Provider
      value={{
        activeStep,
        formik,
        isEdit,
        isLoading,
        paramId,
        serverResult,
        steps,
        viewInstruction,
        goToStep,
        setServerResult,
        toggleInsruction,
        toNextStep,
        toPreviousStep,
      }}
    >
      {children}
    </ManageMasterContext.Provider>
  );
});

/**
 * useManageMasterContext
 *
 * Custom hook for consuming ManageMasterContext. Provides step state, formik,
 * loading flags, and navigation helpers.
 *
 * Usage
 * - Must be called inside <ManageMasterProvider>
 * - Throws error if used outside provider
 *
 * Returns
 * - Object with:
 *   • activeStep (number)
 *   • formik (FormikHelpers)
 *   • isEdit (boolean)
 *   • isLoading (boolean)
 *   • paramId (string|number)
 *   • serverResult (any)
 *   • steps (array)
 *   • goToStep (Function)
 *   • toNextStep (Function)
 *   • toPreviousStep (Function)
 */
export const useManageMasterContext = () => {
  const ctx = useContext(ManageMasterContext);
  if (!ctx) {
    throw new Error("useManageMasterContext must be used inside <ManageMasterProvider>");
  }
  return ctx;
};
